import React from 'react'
import type { ViewMode } from 'src/hooks/use-view-mode'
import { cn } from 'src/utils/style'

interface ItemCollectionProps {
  viewMode: ViewMode
  children: React.ReactNode
  className?: string
  'data-testid'?: string
}

/**
 * Lays out list-page items as a card grid or a stacked list. The card grid
 * uses auto-fill so columns follow the pane width (sidebar/filter pane aware),
 * not the viewport.
 */
export const ItemCollection = ({
  viewMode,
  children,
  className,
  'data-testid': dataTestId,
}: ItemCollectionProps) => (
  <div
    className={cn(
      viewMode === 'card'
        ? 'grid grid-cols-[repeat(auto-fill,minmax(min(100%,18rem),1fr))] gap-4'
        : 'flex flex-col gap-2',
      className,
    )}
    data-testid={dataTestId}
  >
    {children}
  </div>
)

type ItemShellProps = React.HTMLAttributes<HTMLDivElement> & {
  'data-testid'?: string
}

/**
 * List-view row: full-width, content left / value right once the pane is wide
 * enough (expenses/reports look).
 */
export const ListItemRow = ({ className, ...props }: ItemShellProps) => (
  <div
    className={cn(
      'flex min-w-0 flex-col gap-3 rounded-xl border border-border bg-surface px-6 py-4 shadow-sm transition-shadow hover:shadow-md @[480px]:flex-row @[480px]:items-center @[480px]:justify-between',
      props.onClick && 'cursor-pointer',
      className,
    )}
    {...props}
  />
)

/** Card-view tile (reservations look). Fills its grid cell. */
export const ListItemCard = ({ className, ...props }: ItemShellProps) => (
  <div
    className={cn(
      'flex h-full min-w-0 flex-col gap-4 rounded-xl border border-border bg-surface p-4 shadow-sm transition-shadow hover:shadow-lg',
      props.onClick && 'cursor-pointer',
      className,
    )}
    {...props}
  />
)
